/**
 * Text-frame margins (Format Tools › Margins) — ported from the VSTO plugin's
 * MARGIN section (`MarginFunctions.cs` + `FrmSetMargin.cs`).
 *
 * VSTO parity, plus two deliberate improvements:
 *  • VSTO's one-click presets skipped every shape whose text frame was empty
 *    (`TextFrame.HasText`); here they apply to any shape that *has* a text
 *    frame, so an empty placeholder can be zeroed before you type into it.
 *  • VSTO only handled table cells from the Set Margin dialog; here tables are
 *    handled by every margin tool (see `applyTableCellMargins`).
 *
 * Shapes go through the native Office.js `TextFrame` margins (fast, undoable).
 * Tables have no margin API at all, so a table in the selection routes through
 * the OOXML slide rewrite — slower and it clears the selection, which is why
 * it only kicks in when a table is actually selected.
 */
import { hasPpt, ppt } from "../../../utils/formatUtils";
import { EMU_PER_POINT, NS_A } from "../../../const";
import { rewriteActiveSlide } from "./slideRewrite";
import { findSelectedShapeInSpTree } from "./slideOoxml";
import type { MarginPreset, TextMargins } from "../../../types";

/** Points per inch — the Set Margin dialog works in inches, PowerPoint in points. */
export const POINTS_PER_INCH = 72;

/** PowerPoint's out-of-the-box text-frame insets (0.1" sides, 0.05" top/bottom). */
export const DEFAULT_TEXT_MARGINS: TextMargins = { left: 7.2, top: 3.6, right: 7.2, bottom: 3.6 };

/** The margin edges each "0-Margin …" preset zeroes. */
const PRESET_EDGES: Record<MarginPreset, ReadonlyArray<keyof TextMargins>> = {
  all: ["left", "top", "right", "bottom"],
  left: ["left"],
  right: ["right"],
  top: ["top"],
  bottom: ["bottom"],
  leftRight: ["left", "right"],
  topBottom: ["top", "bottom"],
  topLeft: ["top", "left"],
  topRight: ["top", "right"],
  bottomLeft: ["bottom", "left"],
  bottomRight: ["bottom", "right"],
};

/** Office.js TextFrame property backing each logical margin edge. */
const FRAME_PROPERTY: Record<keyof TextMargins, "leftMargin" | "topMargin" | "rightMargin" | "bottomMargin"> = {
  left: "leftMargin",
  top: "topMargin",
  right: "rightMargin",
  bottom: "bottomMargin",
};

/** `a:tcPr` inset attribute backing each logical margin edge. */
const CELL_ATTRIBUTE: Record<keyof TextMargins, "marL" | "marT" | "marR" | "marB"> = {
  left: "marL",
  top: "marT",
  right: "marR",
  bottom: "marB",
};

const ALL_EDGES: ReadonlyArray<keyof TextMargins> = ["left", "top", "right", "bottom"];

/** A partial margin write — only the listed edges are touched. */
export type MarginPatch = Partial<TextMargins>;

/** True when the shape type never carries a text frame we could inset. */
function isTable(shapeType: string): boolean {
  return shapeType.toLowerCase() === "table";
}

/**
 * Write `patch` onto every selected shape's text frame.
 * Returns how many shapes changed and whether the selection held a table.
 */
function applyShapeMargins(patch: MarginPatch): Promise<{ shapes: number; tables: number }> {
  if (!hasPpt()) return Promise.reject(new Error("This tool works only in PowerPoint."));

  return ppt().run(async (context) => {
    const sel = context.presentation.getSelectedShapes();
    sel.load("items");
    await context.sync();
    if (sel.items.length === 0) throw new Error("Select at least one shape first.");

    for (const shape of sel.items) shape.load("type");
    await context.sync();

    let tables = 0;
    const frames: PowerPoint.TextFrame[] = [];
    for (const shape of sel.items) {
      if (isTable(String(shape.type))) {
        tables++;
        continue;
      }
      frames.push(shape.getTextFrameOrNullObject());
    }
    for (const frame of frames) frame.load("isNullObject");
    await context.sync();

    let shapes = 0;
    for (const frame of frames) {
      if (frame.isNullObject) continue; // pictures, lines, charts — nothing to inset
      for (const edge of ALL_EDGES) {
        const value = patch[edge];
        if (value !== undefined) frame[FRAME_PROPERTY[edge]] = value;
      }
      shapes++;
    }
    await context.sync();
    return { shapes, tables };
  });
}

/** Set the inset attributes on one `<a:tcPr>` (created when absent). */
function patchCellProperties(cell: Element, patch: MarginPatch): void {
  let tcPr = cell.getElementsByTagNameNS(NS_A, "tcPr")[0];
  if (!tcPr) {
    // `a:tcPr` is the last child of `a:tc` per the schema, so append is correct.
    tcPr = cell.ownerDocument.createElementNS(NS_A, "a:tcPr");
    cell.append(tcPr);
  }
  for (const edge of ALL_EDGES) {
    const value = patch[edge];
    if (value === undefined) continue;
    tcPr.setAttribute(CELL_ATTRIBUTE[edge], String(Math.round(value * EMU_PER_POINT)));
  }
}

/**
 * Apply `patch` to every cell of each selected table, via the OOXML round-trip.
 *
 * Unlike VSTO this can't narrow to the *selected* cells — Office.js exposes no
 * per-cell selection state — so the whole table is inset, which is what the
 * VSTO dialog also did whenever no individual cell was selected.
 */
async function applyTableCellMargins(patch: MarginPatch): Promise<void> {
  await rewriteActiveSlide(({ selection, spTree }) => {
    const used = new Set<Element>();
    let cells = 0;
    for (const shape of selection) {
      if (!isTable(shape.type)) continue;
      const el = findSelectedShapeInSpTree(spTree, shape, used);
      if (!el) continue;
      used.add(el);
      for (const cell of Array.from(el.getElementsByTagNameNS(NS_A, "tc"))) {
        patchCellProperties(cell, patch);
        cells++;
      }
    }
    return cells;
  }, "Couldn't find the selected table on this slide. Save the file (Ctrl+S) and try again.");
}

/**
 * Apply a margin patch to the selection: shapes natively, tables through the
 * slide rewrite. Throws when nothing in the selection can take margins.
 */
async function applyMargins(patch: MarginPatch): Promise<void> {
  const { shapes, tables } = await applyShapeMargins(patch);
  if (tables > 0) await applyTableCellMargins(patch);
  if (shapes === 0 && tables === 0) {
    throw new Error("None of the selected objects can hold text — select a text box, shape or table.");
  }
}

/** Zero the margin edges named by `preset` on the selection. */
export function zeroMargins(preset: MarginPreset): Promise<void> {
  const patch: MarginPatch = {};
  for (const edge of PRESET_EDGES[preset]) patch[edge] = 0;
  return applyMargins(patch);
}

/** Set all four margins on the selection to explicit point values. */
export function setTextMargins(margins: TextMargins): Promise<void> {
  return applyMargins({
    left: Math.max(0, margins.left),
    top: Math.max(0, margins.top),
    right: Math.max(0, margins.right),
    bottom: Math.max(0, margins.bottom),
  });
}

/**
 * The first selected shape's current margins in points, so the Set Margin
 * panel opens showing what's actually on the slide (VSTO's `FrmSetMargin_Load`).
 * Returns null when nothing usable is selected — the panel then falls back to
 * PowerPoint's defaults rather than surfacing an error.
 */
export async function readSelectionMargins(): Promise<TextMargins | null> {
  if (!hasPpt()) return null;
  try {
    return await ppt().run(async (context) => {
      const sel = context.presentation.getSelectedShapes();
      sel.load("items");
      await context.sync();
      const first = sel.items[0];
      if (!first) return null;

      const frame = first.getTextFrameOrNullObject();
      frame.load("isNullObject,leftMargin,topMargin,rightMargin,bottomMargin");
      await context.sync();
      if (frame.isNullObject) return null;

      return {
        left: frame.leftMargin,
        top: frame.topMargin,
        right: frame.rightMargin,
        bottom: frame.bottomMargin,
      };
    });
  } catch {
    // Reading margins is a convenience prefill — a host hiccup here must not
    // stop the panel from opening on the defaults.
    return null;
  }
}
